import { dogService } from './dogService';
import { PedigreeNode, PedigreeTree } from '@/types/pedigree';
import { Dog } from '@/types/dog';

/**
 * 血統図管理サービスクラス
 */
export class PedigreeService {
  /** デフォルトの祖先取得世代数 */
  private readonly DEFAULT_ANCESTOR_GENERATIONS = 6;
  /**
   * 指定された犬の血統図を取得（3世代分）
   */
  async getPedigreeTree(dogId: string, userId: string): Promise<PedigreeTree | null> {
    try {
      // 本犬の情報を取得
      const subject = await dogService.getDog(dogId, userId);
      if (!subject) {
        return null;
      }

      // 父犬と母犬の血統ノードを取得
      const [father, mother] = await Promise.all([
        this.buildPedigreeNode(subject.fatherId, userId, 2), // 祖父母まで（2世代）
        this.buildPedigreeNode(subject.motherId, userId, 2),
      ]);

      return {
        subject,
        father,
        mother,
      };
    } catch (error) {
      console.error('血統図取得エラー:', error);
      throw new Error('血統図の取得に失敗しました');
    }
  }

  /**
   * 再帰的に血統ノードを構築
   * @param dogId 犬のID
   * @param userId ユーザーID
   * @param depth 残りの取得深度（0になったら終了）
   */
  private async buildPedigreeNode(
    dogId: string | undefined,
    userId: string,
    depth: number
  ): Promise<PedigreeNode | null> {
    // IDが未設定または深度が0なら終了
    if (!dogId || depth === 0) {
      return null;
    }

    try {
      // 犬の情報を取得
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        return null;
      }

      // 親がいない場合や深度が1の場合は、親情報を取得しない
      if (depth === 1 || (!dog.fatherId && !dog.motherId)) {
        return {
          dog,
        };
      }

      // 再帰的に親の血統ノードを構築
      const [father, mother] = await Promise.all([
        this.buildPedigreeNode(dog.fatherId, userId, depth - 1),
        this.buildPedigreeNode(dog.motherId, userId, depth - 1),
      ]);

      const node: PedigreeNode = {
        dog,
      };

      if (father !== null) {
        node.father = father;
      }
      if (mother !== null) {
        node.mother = mother;
      }

      return node;
    } catch (error) {
      console.error(`血統ノード構築エラー (dogId: ${dogId}):`, error);
      return null;
    }
  }

  /**
   * 血統図の完全性をチェック
   * @returns 親犬情報の存在状況
   */
  checkPedigreeCompleteness(pedigree: PedigreeTree): {
    hasFather: boolean;
    hasMother: boolean;
    hasGrandparents: boolean;
  } {
    const hasFather = pedigree.father !== null;
    const hasMother = pedigree.mother !== null;

    const hasGrandparents =
      (hasFather && (pedigree.father?.father !== undefined || pedigree.father?.mother !== undefined)) ||
      (hasMother && (pedigree.mother?.father !== undefined || pedigree.mother?.mother !== undefined));

    return {
      hasFather,
      hasMother,
      hasGrandparents,
    };
  }

  /**
   * 指定した世代数までの祖先マップを取得
   * AI交配提案の近親係数計算に使用
   * @param dogId 対象犬のID
   * @param userId ユーザーID
   * @param generations 取得する世代数（デフォルト6）
   * @returns 祖先ID → Dog のマップ
   */
  async getAncestorMap(
    dogId: string,
    userId: string,
    generations: number = this.DEFAULT_ANCESTOR_GENERATIONS
  ): Promise<Map<string, Dog>> {
    const ancestorMap = new Map<string, Dog>();
    const visited = new Set<string>();

    await this.collectAncestors(dogId, userId, generations, ancestorMap, visited);

    return ancestorMap;
  }

  /**
   * 複数の犬の祖先マップを一括取得
   * @param dogIds 対象犬のIDリスト
   * @param userId ユーザーID
   * @param generations 取得する世代数（デフォルト6）
   * @returns 統合された祖先マップ
   */
  async getAncestorMapForMultipleDogs(
    dogIds: string[],
    userId: string,
    generations: number = this.DEFAULT_ANCESTOR_GENERATIONS
  ): Promise<Map<string, Dog>> {
    const ancestorMap = new Map<string, Dog>();
    const visited = new Set<string>();

    // 全犬の祖先を並列で収集
    await Promise.all(
      dogIds.map((dogId) =>
        this.collectAncestors(dogId, userId, generations, ancestorMap, visited)
      )
    );

    return ancestorMap;
  }

  /**
   * 再帰的に祖先を収集
   * @param dogId 犬のID
   * @param userId ユーザーID
   * @param remainingDepth 残りの取得深度
   * @param ancestorMap 祖先マップ（結果格納用）
   * @param visited 訪問済みIDセット（循環防止）
   */
  private async collectAncestors(
    dogId: string,
    userId: string,
    remainingDepth: number,
    ancestorMap: Map<string, Dog>,
    visited: Set<string>
  ): Promise<void> {
    // 深度0、または既に訪問済みなら終了
    if (remainingDepth <= 0 || visited.has(dogId)) {
      return;
    }

    visited.add(dogId);

    try {
      // 犬の情報を取得
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        return;
      }

      // 祖先マップに追加
      ancestorMap.set(dogId, dog);

      // 親がいる場合は再帰的に収集
      const parentPromises: Promise<void>[] = [];

      if (dog.fatherId) {
        parentPromises.push(
          this.collectAncestors(
            dog.fatherId,
            userId,
            remainingDepth - 1,
            ancestorMap,
            visited
          )
        );
      }

      if (dog.motherId) {
        parentPromises.push(
          this.collectAncestors(
            dog.motherId,
            userId,
            remainingDepth - 1,
            ancestorMap,
            visited
          )
        );
      }

      // 並列で親を取得
      await Promise.all(parentPromises);
    } catch (error) {
      console.error(`祖先収集エラー (dogId: ${dogId}):`, error);
      // エラーが発生しても他の祖先の収集を続行
    }
  }
}

// シングルトンインスタンスをエクスポート
export const pedigreeService = new PedigreeService();
